import {
  ClockCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip as ChartTooltip,
} from "chart.js";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Tooltip as AntTooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import AppContext from "../../../context/app/context";
import {
  IStoreAcceptanceTimeStat,
  IStoreAcceptanceTimeStats,
} from "../../../models/statistcs/respons";
import Table from "../../general/table-components/table";
import styles from "./style.module.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
);

interface IProps {
  stats?: IStoreAcceptanceTimeStats;
  onChange: (stats: IStoreAcceptanceTimeStats) => void;
  hideDateControls?: boolean;
}

interface IStoreAcceptanceTimeRow extends IStoreAcceptanceTimeStat {
  key: string;
  isOutside: boolean;
}

type SortOption =
  | "highest_average"
  | "lowest_average"
  | "highest_order_count"
  | "area_name";

type FieldErrors = Partial<Record<"startDate" | "endDate", string>>;

const DATE_FORMAT = "YYYY-MM-DD";

const extractFieldError = (error: any, field: "startDate" | "endDate") => {
  const errors = error?.response?.data?.errors;
  const value = errors?.[field];

  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  if (typeof value === "string") return value;

  return undefined;
};

const getNullableNumber = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const normalizeDatePickerValue = (value: string | string[]) =>
  Array.isArray(value) ? value[0] : value || undefined;

const StoreAcceptanceTimeStats: FC<IProps> = ({
  stats,
  onChange,
  hideDateControls,
}) => {
  const { t, i18n } = useTranslation();
  const { theme } = useContext(AppContext);
  const [startDate, setStartDate] = useState<string | undefined>(
    stats?.startDate,
  );
  const [endDate, setEndDate] = useState<string | undefined>(stats?.endDate);
  const [sortOption, setSortOption] = useState<SortOption>("highest_average");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language],
  );

  useEffect(() => {
    setStartDate(stats?.startDate);
    setEndDate(stats?.endDate);
  }, [stats?.endDate, stats?.startDate]);

  const rows = useMemo<IStoreAcceptanceTimeRow[]>(() => {
    if (!stats) return [];

    const buildRow = (
      item: IStoreAcceptanceTimeStat,
      index: number,
      isOutside = false,
    ): IStoreAcceptanceTimeRow => ({
      ...item,
      key: isOutside
        ? "outside-areas"
        : `store-acceptance-area-${item.areaId ?? index}`,
      isOutside,
    });

    const compareRows = (
      a: IStoreAcceptanceTimeRow,
      b: IStoreAcceptanceTimeRow,
    ) => {
      if (sortOption === "highest_order_count") {
        return b.orderCount - a.orderCount;
      }

      if (sortOption === "area_name") {
        return a.areaName.localeCompare(b.areaName, i18n.language);
      }

      const aAverage = getNullableNumber(a.averageAcceptanceMinutes);
      const bAverage = getNullableNumber(b.averageAcceptanceMinutes);

      if (aAverage === null && bAverage === null) return 0;
      if (aAverage === null) return 1;
      if (bAverage === null) return -1;

      return sortOption === "lowest_average"
        ? aAverage - bAverage
        : bAverage - aAverage;
    };

    const areaRows = (stats.areas || [])
      .map((area, index) => buildRow(area, index))
      .sort(compareRows);

    return stats.outsideAreas
      ? [...areaRows, buildRow(stats.outsideAreas, -1, true)]
      : areaRows;
  }, [i18n.language, sortOption, stats]);

  const chartRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          !row.isOutside &&
          getNullableNumber(row.averageAcceptanceMinutes) !== null,
      ),
    [rows],
  );

  if (!stats) return null;

  const disabledFutureDate = (date: Dayjs) =>
    date && date.isAfter(dayjs(), "day");

  const formatNumber = (value: number | null | undefined) => {
    const number = getNullableNumber(value);
    return number === null ? "-" : numberFormatter.format(number);
  };

  const formatMinutes = (value: number | null | undefined) => {
    const number = getNullableNumber(value);
    return number === null
      ? "-"
      : `${numberFormatter.format(number)} ${t("minutes")}`;
  };

  const validateDates = () => {
    const errors: FieldErrors = {};
    const today = dayjs();
    const start = startDate ? dayjs(startDate, DATE_FORMAT, true) : null;
    const end = endDate ? dayjs(endDate, DATE_FORMAT, true) : null;

    if (startDate && (!start?.isValid() || start.isAfter(today, "day"))) {
      errors.startDate = t("invalid_date");
    }

    if (endDate && (!end?.isValid() || end.isAfter(today, "day"))) {
      errors.endDate = t("invalid_date");
    }

    if (start?.isValid() && end?.isValid() && end.isBefore(start, "day")) {
      errors.endDate = t("end_date_before_start_date");
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const refreshStoreAcceptanceTimeStats = async () => {
    if (!validateDates()) return;

    try {
      setLoading(true);
      setFieldErrors({});

      return;
    } catch (error) {
      setFieldErrors({
        startDate: extractFieldError(error, "startDate"),
        endDate: extractFieldError(error, "endDate"),
      });
    } finally {
      setLoading(false);
    }
  };

  const chartData = {
    labels: chartRows.map((row) => row.areaName),
    datasets: [
      {
        label: t("average_acceptance_minutes"),
        data: chartRows.map((row) => row.averageAcceptanceMinutes ?? 0),
        backgroundColor: "rgba(24, 144, 255, 0.45)",
        borderColor: "rgba(24, 144, 255, 0.95)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const chartTextColor =
    theme === "dark" ? "rgba(255, 255, 255, 0.78)" : "rgba(0, 0, 0, 0.72)";
  const chartGridColor =
    theme === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)";

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) =>
            `${t("average")}: ${numberFormatter.format(context.parsed.y)} ${t(
              "minutes",
            )}`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: chartTextColor,
          maxRotation: 35,
          minRotation: 0,
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: chartTextColor,
        },
        grid: {
          color: chartGridColor,
        },
      },
    },
  };

  const columns: ColumnsType<IStoreAcceptanceTimeRow> = [
    {
      title: t("area"),
      dataIndex: "areaName",
      key: "areaName",
      ellipsis: true,
      render: (name: string, row) => (
        <AntTooltip title={row.isOutside ? t("outside_areas") : name}>
          <div className={styles.areaName}>
            <span>{row.isOutside ? t("outside_areas") : name}</span>
            {row.isOutside && <Tag color="blue">{t("outside_areas")}</Tag>}
          </div>
        </AntTooltip>
      ),
    },
    {
      title: t("orders"),
      dataIndex: "orderCount",
      key: "orderCount",
      width: 110,
      sorter: (a, b) => a.orderCount - b.orderCount,
      render: (value: number) => (
        <span className={styles.count}>{numberFormatter.format(value)}</span>
      ),
    },
    {
      title: t("average"),
      dataIndex: "averageAcceptanceMinutes",
      key: "averageAcceptanceMinutes",
      width: 130,
      sorter: (a, b) =>
        (a.averageAcceptanceMinutes ?? -1) - (b.averageAcceptanceMinutes ?? -1),
      render: formatMinutes,
    },
    {
      title: t("median"),
      dataIndex: "medianAcceptanceMinutes",
      key: "medianAcceptanceMinutes",
      width: 130,
      sorter: (a, b) =>
        (a.medianAcceptanceMinutes ?? -1) - (b.medianAcceptanceMinutes ?? -1),
      render: formatMinutes,
    },
    {
      title: t("min_label"),
      dataIndex: "minAcceptanceMinutes",
      key: "minAcceptanceMinutes",
      width: 110,
      render: formatMinutes,
    },
    {
      title: t("max_label"),
      dataIndex: "maxAcceptanceMinutes",
      key: "maxAcceptanceMinutes",
      width: 110,
      render: formatMinutes,
    },
  ];

  return (
    <div className={styles.container}>
      <Divider orientation="center">{t("store_acceptance_time_stats")}</Divider>
      <Card className={styles.panel} bodyStyle={{ padding: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={hideDateControls ? 24 : 10}>
            <div className={styles.metricGrid}>
              <div className={`${styles.metricTile} ${styles.primaryMetric}`}>
                <Statistic
                  title={
                    <Space>
                      <ClockCircleOutlined />
                      <AntTooltip title={t("store_acceptance_time_tooltip")}>
                        <span>{t("average_store_acceptance_time")}</span>
                      </AntTooltip>
                    </Space>
                  }
                  value={formatNumber(stats.averageAcceptanceMinutes)}
                  suffix={
                    getNullableNumber(stats.averageAcceptanceMinutes) === null
                      ? undefined
                      : t("minutes")
                  }
                />
              </div>
              <div className={styles.metricTile}>
                <Statistic
                  title={t("included_order_count")}
                  value={numberFormatter.format(stats.includedOrderCount)}
                />
              </div>
              <div className={`${styles.metricTile} ${styles.warningMetric}`}>
                <Statistic
                  title={
                    <Space>
                      <WarningOutlined />
                      <AntTooltip
                        title={t("excluded_store_acceptance_orders_tooltip", {
                          count: stats.excludedOrderCount,
                        })}
                      >
                        <span>{t("data_quality_exclusions")}</span>
                      </AntTooltip>
                    </Space>
                  }
                  value={numberFormatter.format(stats.excludedOrderCount)}
                />
              </div>
            </div>
            <div className={styles.period}>
              {stats.startDate} - {stats.endDate}
            </div>
          </Col>
          {!hideDateControls && (
            <Col xs={24} lg={14}>
              <Row gutter={[12, 8]} align="top">
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={t("startDate")}
                    validateStatus={fieldErrors.startDate ? "error" : undefined}
                    help={fieldErrors.startDate}
                  >
                    <DatePicker
                      allowClear
                      disabledDate={disabledFutureDate}
                      format={DATE_FORMAT}
                      value={startDate ? dayjs(startDate) : undefined}
                      onChange={(_, value) =>
                        setStartDate(normalizeDatePickerValue(value))
                      }
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={t("endDate")}
                    validateStatus={fieldErrors.endDate ? "error" : undefined}
                    help={fieldErrors.endDate}
                  >
                    <DatePicker
                      allowClear
                      disabledDate={disabledFutureDate}
                      format={DATE_FORMAT}
                      value={endDate ? dayjs(endDate) : undefined}
                      onChange={(_, value) =>
                        setEndDate(normalizeDatePickerValue(value))
                      }
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label={t("sort_by")}>
                    <Select
                      value={sortOption}
                      onChange={setSortOption}
                      options={[
                        {
                          value: "highest_average",
                          label: t("highest_average_first"),
                        },
                        {
                          value: "lowest_average",
                          label: t("lowest_average_first"),
                        },
                        {
                          value: "highest_order_count",
                          label: t("highest_order_count_first"),
                        },
                        { value: "area_name", label: t("area_name") },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={refreshStoreAcceptanceTimeStats}
              >
                {t("refresh")}
              </Button>
            </Col>
          )}
        </Row>

        <div className={styles.chartPanel}>
          <Typography.Text strong>
            {t("store_acceptance_time_by_area")}
          </Typography.Text>
          <div className={styles.chart}>
            {chartRows.length > 0 ? (
              <Bar data={chartData} options={chartOptions} />
            ) : (
              <div className={styles.emptyChart}>{t("no_data")}</div>
            )}
          </div>
        </div>

        <div className={styles.tablePanel}>
          <Table
            rowKey="key"
            columns={columns}
            dataSource={rows}
            pagination={false}
            rowClassName={(row) => (row.isOutside ? styles.outsideRow : "")}
            scroll={{ x: 860 }}
          />
        </div>
      </Card>
    </div>
  );
};

export default StoreAcceptanceTimeStats;
